import React, { Fragment, useState, useRef, useEffect } from 'react'
import { object, bool, func, string, array } from 'prop-types'
import { renderContentValues } from '../../redux/configuration/utilities'
import Counter from '../buttons/Counter'

const KennelQuantitiesCard = ({
  labels,
  content,
  kennels,
  selections,
  accommodationsContent,
  startingFrom,
  confirmButtonText,
  closeButtonText,
  isOneWay,
  onCloseClick,
  onConfirmClick
}) => {
  const kennelsDetails = Object.values(kennels).map(x => x.details)
  const initialState = selections
    ? selections
    : [
        {
          resourceCode: '',
          departure: 0,
          return: 0
        }
      ]
  const [selectedQuantities, setSelectedQuantities] = useState(initialState)
  const modalRef = useRef(null)
  useEffect(() => {
    if (!modalRef.current) return
    const modalNode = modalRef.current
    const focusableElements = modalNode.querySelectorAll(
      'a[href], button, input, select, [tabindex]:not([tabindex="-1"])'
    )
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]
    function handleKeyDown(event) {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement.focus()
          }
        }
      } else if (event.key === 'Escape') {
        onCloseClick()
      }
    }
    modalNode.addEventListener('keydown', handleKeyDown)
    if (firstElement) firstElement.focus()
    return () => {
      modalNode.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line
  }, [])

  const getSelectOptions = index => {
    const { resourceCode } = selectedQuantities[index]
    const usedResourceCodes = selectedQuantities.map(x => x.resourceCode)
    const options = kennelsDetails
      .map(details => {
        const key = details.resourceCode
        const departureFreeCapacity =
          +kennels?.[key]?.departure?.freeCapacity ?? 0
        const returnFreeCapacity = +kennels?.[key]?.return?.freeCapacity ?? 0
        if (departureFreeCapacity <= 0 && returnFreeCapacity <= 0) return null

        if (key === resourceCode || !usedResourceCodes.includes(key)) {
          const kennelContent = accommodationsContent?.accommodations[key]

          if (!kennelContent) {
            return null
          }

          return (
            <option key={key} value={key}>
              {kennelContent.title}
            </option>
          )
        } else {
          return null
        }
      })
      .filter(e => !!e)
    return (
      <Fragment>
        <option key="_" value="">
          {`- ${labels.selectSize} -`}
        </option>
        {options}
      </Fragment>
    )
  }

  const handleIncrement = (index, direction) => {
    const newState = [...selectedQuantities]
    const { resourceCode } = newState[index]
    if (resourceCode) {
      const freeCapacity = +(
        kennels?.[resourceCode]?.[direction]?.freeCapacity ?? 0
      )
      const currentValue = newState[index][direction]
      if (currentValue < freeCapacity) {
        newState[index][direction]++
        setSelectedQuantities(newState)
      }
    }
  }

  const handleDecrement = (index, direction) => {
    const newState = [...selectedQuantities]
    if (newState[index].resourceCode) {
      newState[index][direction]--
      setSelectedQuantities(newState)
    }
  }

  const updateSelect = (index, newCode) => {
    const newState = [...selectedQuantities]
    newState[index].resourceCode = newCode
    newState[index].departure = 0
    newState[index].return = 0
    setSelectedQuantities(newState)
  }

  const canAddKennelRow = selectedQuantities.length < kennelsDetails.length
  const addKennelRow = () => {
    const newState = [
      ...selectedQuantities,
      { resourceCode: '', departure: 0, return: 0 }
    ]
    setSelectedQuantities(newState)
  }

  const handleConfirmClick = () => {
    const selectionObj = selectedQuantities.reduce(
      (acc, cur) => {
        const details = kennelsDetails.find(
          x => x.resourceCode === cur.resourceCode
        )
        acc.departure[cur.resourceCode] = {
          count: cur.departure,
          details
        }
        acc.return[cur.resourceCode] = {
          count: cur.return,
          details
        }
        return acc
      },
      {
        departure: {},
        return: {}
      }
    )
    onConfirmClick(selectionObj)
  }

  let priceElement = null
  if (startingFrom) {
    priceElement = (
      <div className="extras__card-start pad">
        <div className="extras__card-start-label">{labels.startingFrom}</div>
        <div className="extras__card-start-value">{startingFrom}</div>
        <div className="extras__card-start-label">{labels.perKennel}</div>
      </div>
    )
  }

  const quantitySelectGrid = (
    <div className={`extras__card-kennel-grid-${isOneWay ? 'ow' : 'rt'}`}>
      <div className="extras__card-grid-label">{labels.size}</div>
      <div className="extras__card-grid-label">{labels.departureRoute}</div>
      {!isOneWay && (
        <div className="extras__card-grid-label">{labels.returnRoute}</div>
      )}
      {selectedQuantities.map((selection, index) => {
        const resourceCode = selection.resourceCode
        const curDeparture = selection.departure
        const curReturn = selection.return
        const kennelContent = accommodationsContent.accommodations[resourceCode]
        const departureFreeCapacity =
          +kennels[resourceCode]?.departure?.freeCapacity ?? 0
        const returnFreeCapacity =
          +kennels[resourceCode]?.return?.freeCapacity ?? 0

        return (
          <Fragment key={`sel_${index}`}>
            <select
              value={resourceCode}
              onChange={e => updateSelect(index, e.target.value)}
              data-test="drop-size"
            >
              {getSelectOptions(index)}
            </select>
            <Counter
              value={
                resourceCode && kennels[resourceCode]?.departure
                  ? curDeparture
                  : 'x'
              }
              handleIncrement={() => handleIncrement(index, 'departure')}
              resource="DepartureKennel"
              handleDecrement={() => handleDecrement(index, 'departure')}
              disabled={
                !resourceCode ||
                !kennels[resourceCode]?.departure ||
                departureFreeCapacity <= 0
              }
              disableIncrement={
                resourceCode && !kennels[resourceCode]?.departure
              }
              disableDecrement={
                curDeparture === 0 ||
                (resourceCode && !kennels[resourceCode]?.departure)
              }
              size="small"
              increaseLabel="Add kennel to Departure Trip"
              reduceLabel="Remove kennel from Departure Trip"
            />
            {!isOneWay && (
              <Counter
                value={
                  resourceCode && kennels[resourceCode]?.return
                    ? curReturn
                    : 'x'
                }
                handleIncrement={() => handleIncrement(index, 'return')}
                resource="ReturnKennel"
                handleDecrement={() => handleDecrement(index, 'return')}
                disabled={
                  !resourceCode ||
                  !kennels[resourceCode]?.return ||
                  returnFreeCapacity <= 0
                }
                disableIncrement={
                  resourceCode && !kennels[resourceCode]?.return
                }
                disableDecrement={
                  curReturn === 0 ||
                  (resourceCode && !kennels[resourceCode]?.return)
                }
                size="small"
                increaseLabel="Add kennel to Return Trip"
                reduceLabel="Remove kennel from Return Trip"
              />
            )}
            {!!kennelContent && (
              <div className="extras__card-kennel-grid-subtext">
                {kennelContent.textBlocks[0]}
              </div>
            )}
          </Fragment>
        )
      })}
    </div>
  )

  return (
    <div className="extras__card" ref={modalRef} data-test="cont-kennel">
      <div
        className="extras__card-close"
        onClick={onCloseClick}
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault()
            onCloseClick()
          }
        }}
      >
        {closeButtonText}
      </div>
      <div className="extras__card-content">
        <div>{renderContentValues(content)}</div>
        <div className="extras__card-bottom">{priceElement}</div>
        {quantitySelectGrid}
        <div className="extras__card-grid-label add-btn">
          {canAddKennelRow && (
            <span onClick={addKennelRow} data-test="btn-extraKennel">
              {labels.extraKennel}
            </span>
          )}
        </div>
        <button
          className="extras__card-btn-confirm btn btn-primary"
          onClick={handleConfirmClick}
          data-test="btn-addKenels"
        >
          {confirmButtonText}
        </button>
      </div>
    </div>
  )
}

KennelQuantitiesCard.propTypes = {
  labels: object.isRequired,
  content: array.isRequired,
  kennels: object.isRequired,
  selections: array,
  accommodationsContent: object.isRequired,
  startingFrom: string,
  confirmButtonText: string.isRequired,
  closeButtonText: string.isRequired,
  isOneWay: bool.isRequired,
  onCloseClick: func.isRequired,
  onConfirmClick: func.isRequired
}

export default KennelQuantitiesCard
